<template>
  <div class="anamnesis-subset">
    <!-- Toolbar réutilisable -->
    <SubsetToolbar
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      :is-card-view="viewMode === 'default'"
      :search-query="searchQuery"
      search-placeholder="Rechercher une entité matérielle..."
      create-button-text="Nouvelle entité"
      :show-create-button="showActions"
      @update:sort-by="handleUpdateSortBy"
      @update:search-query="handleUpdateSearchQuery"
      @toggle-sort-direction="handleToggleSortDirection"
      @toggle-view="handleToggleView"
      @create="$emit('create')"
    >
      <!-- Slot filtres : Dropdowns Types et Matériaux -->
      <template #filters>
        <!-- Filtre par type -->
        <div class="relative">
          <button
            class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
            :class="[
              selectedTypes.length > 0
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            ]"
            @click="handleToggleTypeDropdown"
          >
            <Amphora class="h-4 w-4" />
            <span>Types ({{ selectedTypes.length }})</span>
            <ChevronDownIcon class="h-4 w-4" :class="{ 'transform rotate-180': showTypeDropdown }" />
          </button>

          <!-- Dropdown pour les types -->
          <div
            v-if="showTypeDropdown" 
            class="absolute z-50 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 divide-y divide-gray-100"
          >
            <div class="px-3 py-2 flex items-center justify-between">
              <button 
                class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                @click="handleToggleAllTypes"
              >
                {{ allTypesSelected ? 'Tout désélectionner' : 'Tout sélectionner' }}
              </button>
              <span class="text-xs text-gray-500">{{ selectedTypes.length }} sélectionné(s)</span>
            </div>
            <div class="py-1">
              <div class="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer" @click="handleToggleUnspecifiedTypes">
                <span class="text-sm text-gray-700 font-medium min-w-0 flex-1 mr-4">Non spécifié</span>
                <div class="flex-shrink-0 w-5">
                  <input
                    type="checkbox" 
                    :checked="showUnspecifiedTypes"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    @change="handleToggleUnspecifiedTypes"
                    @click.stop
                  />
                </div>
              </div>
            </div>
            <div class="max-h-48 overflow-y-auto py-1">
              <div
                v-for="type in availableTypes" 
                :key="type" 
                class="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer group"
                @click="handleToggleType(type || '')"
              >
                <span class="text-sm text-gray-700 group-hover:text-gray-900 min-w-0 flex-1 mr-4">{{ type }}</span>
                <div class="flex-shrink-0 w-5">
                  <input
                    type="checkbox" 
                    :checked="selectedTypes.includes(type || '')"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    @change="handleToggleType(type || '')"
                    @click.stop
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filtre par matériau -->
        <div class="relative">
          <button
            class="px-3 py-1.5 rounded-full text-sm transition-colors duration-200 flex items-center space-x-1.5"
            :class="[
              selectedMaterials.length > 0
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
            ]"
            @click="handleToggleMaterialDropdown"
          >
            <Package class="h-4 w-4" />
            <span>Matériaux ({{ selectedMaterials.length }})</span>
            <ChevronDownIcon class="h-4 w-4" :class="{ 'transform rotate-180': showMaterialDropdown }" />
          </button>

          <!-- Dropdown pour les matériaux -->
          <div
            v-if="showMaterialDropdown" 
            class="absolute z-50 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2 divide-y divide-gray-100"
          >
            <div class="px-3 py-2 flex items-center justify-between">
              <button 
                class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                @click="handleToggleAllMaterials"
              >
                {{ allMaterialsSelected ? 'Tout désélectionner' : 'Tout sélectionner' }}
              </button>
              <span class="text-xs text-gray-500">{{ selectedMaterials.length }} sélectionné(s)</span>
            </div>
            <div class="py-1">
              <div class="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer" @click="handleToggleUnspecifiedMaterials">
                <span class="text-sm text-gray-700 font-medium min-w-0 flex-1 mr-4">Non spécifié</span>
                <div class="flex-shrink-0 w-5">
                  <input
                    type="checkbox" 
                    :checked="showUnspecifiedMaterials"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    @change="handleToggleUnspecifiedMaterials"
                    @click.stop
                  />
                </div>
              </div>
            </div>
            <div class="max-h-48 overflow-y-auto py-1">
              <div
                v-for="material in availableMaterials" 
                :key="material" 
                class="flex items-center px-3 py-1.5 hover:bg-gray-50 cursor-pointer group"
                @click="handleToggleMaterial(material || '')"
              >
                <span class="text-sm text-gray-700 group-hover:text-gray-900 min-w-0 flex-1 mr-4">{{ material }}</span>
                <div class="flex-shrink-0 w-5">
                  <input
                    type="checkbox" 
                    :checked="selectedMaterials.includes(material || '')"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    @change="handleToggleMaterial(material || '')"
                    @click.stop
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </SubsetToolbar>

    <!-- Liste des entités -->
    <div
      v-if="paginatedEntities.length > 0"
      :class="{
        'anamnesis-grid': viewMode === 'default',
        'anamnesis-table': viewMode === 'table'
      }"
      class="view-transition"
    >
      <!-- En-tête du tableau (mode table uniquement) -->
      <div v-if="viewMode === 'table'" class="flex items-center px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
        <div class="flex-[1.5] min-w-0">Entité matérielle</div>
        <div class="hidden md:flex flex-1 min-w-0 px-3">Type</div>
        <div class="hidden lg:flex flex-1 min-w-0 px-3">Matériau</div>
        <div class="min-w-0 px-3">Mémos</div>
        <div class="ml-auto">Actions</div>
      </div>
      
      <MaterialEntityCard
        v-for="entity in paginatedEntities"
        :key="entity.uuid"
        :material-entity="entity"
        :view-mode="viewMode"
        :show-actions="showActions"
        :show-edit-button="showEditButton"
        :show-delete-button="showDeleteButton"
        :is-owned="isMaterialEntityOwned(entity)"
        :is-public="entity.is_public"
        :show-ownership-badge="true"
        :user-id="entity.user_id"
        @edit="$emit('edit', entity)"
        @delete="$emit('delete', entity)"
      />
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500">Aucune entité matérielle trouvée</p>
    </div>

    <!-- Compteur d'entités -->
    <div class="flex justify-end mt-4 mb-2">
      <p class="text-sm text-gray-400">
        {{ filteredEntities.length }} entité{{ filteredEntities.length > 1 ? 's' : '' }} matérielle{{ filteredEntities.length > 1 ? 's' : '' }} trouvée{{ filteredEntities.length > 1 ? 's' : '' }}
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="filteredEntities.length > itemsPerPage" class="mt-4 flex justify-center space-x-2">
      <button
        :disabled="currentPage === 1"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === 1 ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handlePreviousPage"
      >
        Précédent
      </button>
      <button
        v-for="page in totalPages"
        :key="page"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
      <button
        :disabled="currentPage === totalPages"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === totalPages ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handleNextPage"
      >
        Suivant
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
    Amphora,
    ChevronDown as ChevronDownIcon,
    Package,
} from 'lucide-vue-next';
import type { ComputedRef, Ref } from 'vue';
import { computed, onMounted, ref, watch } from 'vue';
import type { MaterialEntityWithMemos } from '../../api/types/';
import { useAuthStore } from '../../stores/auth';
import type { SortOption } from '../UI/SubsetToolbar.vue';
import SubsetToolbar from '../UI/SubsetToolbar.vue';
import MaterialEntityCard from '../cards/MaterialEntityCard.vue';

// Types stricts
type SortBy = 'appellation' | 'type' | 'material' | 'memos_count' | 'date';
type SortDirection = 'asc' | 'desc';

// Props avec types stricts
interface Props {
  items: MaterialEntityWithMemos[];
  showActions?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  itemsPerPage?: number;
  defaultSort?: SortBy;
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  showEditButton: true,
  showDeleteButton: true,
  itemsPerPage: 20,
  defaultSort: 'appellation',
});

// Events avec types stricts
defineEmits<{
  create: [];
  edit: [entity: MaterialEntityWithMemos];
  delete: [entity: MaterialEntityWithMemos];
}>();

// Store auth
const authStore = useAuthStore();

// État réactif avec types stricts
const storedView: string | null = localStorage.getItem('materialEntitiesViewMode');
const viewMode: Ref<'default' | 'table'> = ref(storedView === 'table' ? 'table' : 'default');
const searchQuery: Ref<string> = ref('');
const sortBy: Ref<SortBy> = ref(props.defaultSort);
const sortDirection: Ref<SortDirection> = ref('asc');
const selectedTypes: Ref<string[]> = ref([]);
const selectedMaterials: Ref<string[]> = ref([]);
const showTypeDropdown: Ref<boolean> = ref(false);
const showMaterialDropdown: Ref<boolean> = ref(false);
const showUnspecifiedTypes: Ref<boolean> = ref(false);
const showUnspecifiedMaterials: Ref<boolean> = ref(false);
const currentPage: Ref<number> = ref(1);

// Options de tri avec types stricts
const sortOptions: ComputedRef<SortOption[]> = computed(() => [
  { value: 'appellation', label: 'Nom (Appellation)' },
  { value: 'type', label: 'Type' },
  { value: 'material', label: 'Matériau' },
  { value: 'memos_count', label: 'Nombre de mémos' },
  { value: 'date', label: 'Date de création' },
]);

// Types et matériaux disponibles
const availableTypes: ComputedRef<string[]> = computed(() => {
  const types = new Set(props.items.map((item: MaterialEntityWithMemos) => item.type).filter(Boolean) as string[]);
  return Array.from(types).sort();
});

const availableMaterials: ComputedRef<string[]> = computed(() => {
  const materials = new Set(props.items.map((item: MaterialEntityWithMemos) => item.material).filter(Boolean) as string[]);
  return Array.from(materials).sort();
});

// Computed pour vérifier si tous les types/matériaux sont sélectionnés
const allTypesSelected: ComputedRef<boolean> = computed(() => 
  selectedTypes.value.length === availableTypes.value.length
);

const allMaterialsSelected: ComputedRef<boolean> = computed(() => 
  selectedMaterials.value.length === availableMaterials.value.length
);

// Handlers avec types de retour explicites
const handleUpdateSortBy = (value: string): void => {
  sortBy.value = value as SortBy;
};

const handleUpdateSearchQuery = (value: string): void => {
  searchQuery.value = value;
};

const handleToggleSortDirection = (): void => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
};

const handleToggleView = (): void => {
  viewMode.value = viewMode.value === 'default' ? 'table' : 'default';
  localStorage.setItem('materialEntitiesViewMode', viewMode.value);
};

const handleToggleTypeDropdown = (): void => {
  showTypeDropdown.value = !showTypeDropdown.value;
  if (showTypeDropdown.value) {
    showMaterialDropdown.value = false;
  }
};

const handleToggleMaterialDropdown = (): void => {
  showMaterialDropdown.value = !showMaterialDropdown.value;
  if (showMaterialDropdown.value) {
    showTypeDropdown.value = false;
  }
};

const handleToggleType = (type: string): void => {
  const index: number = selectedTypes.value.indexOf(type);
  if (index === -1) {
    selectedTypes.value.push(type);
  } else {
    selectedTypes.value.splice(index, 1);
  }
};

const handleToggleMaterial = (material: string): void => {
  const index: number = selectedMaterials.value.indexOf(material);
  if (index === -1) {
    selectedMaterials.value.push(material);
  } else {
    selectedMaterials.value.splice(index, 1);
  }
};

const handleToggleAllTypes = (): void => {
  if (allTypesSelected.value) {
    selectedTypes.value = [];
  } else {
    selectedTypes.value = [...availableTypes.value];
  }
};

const handleToggleAllMaterials = (): void => {
  if (allMaterialsSelected.value) {
    selectedMaterials.value = [];
  } else {
    selectedMaterials.value = [...availableMaterials.value];
  }
};

const handleToggleUnspecifiedTypes = (): void => {
  showUnspecifiedTypes.value = !showUnspecifiedTypes.value;
};

const handleToggleUnspecifiedMaterials = (): void => {
  showUnspecifiedMaterials.value = !showUnspecifiedMaterials.value;
};

const handlePreviousPage = (): void => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleNextPage = (): void => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Fonction pour vérifier la propriété d'une entité matérielle
const isMaterialEntityOwned = (entity: MaterialEntityWithMemos): boolean => {
  return authStore.isAssetOwner(entity);
};

// Fonction de recherche typée correctement
const searchInEntity = (entity: MaterialEntityWithMemos, query: string): boolean => {
  const searchTerm = query.toLowerCase();
  
  // Recherche dans les champs principaux
  if (entity.appellation?.toLowerCase().includes(searchTerm)) return true;
  if (entity.type?.toLowerCase().includes(searchTerm)) return true;
  if (entity.material?.toLowerCase().includes(searchTerm)) return true;
  if (entity.identifier?.toLowerCase().includes(searchTerm)) return true;
  
  // Recherche dans l'UUID
  if (entity.uuid?.toLowerCase().includes(searchTerm)) return true;
  
  return false;
};

// Filtrage et tri avec types stricts
const filteredEntities: ComputedRef<MaterialEntityWithMemos[]> = computed(() => {
  let result: MaterialEntityWithMemos[] = [...props.items];

  // Filtrage par types sélectionnés
  if (selectedTypes.value.length > 0 || showUnspecifiedTypes.value) {
    result = result.filter((entity: MaterialEntityWithMemos): boolean => {
      if (showUnspecifiedTypes.value && !entity.type) return true;
      return entity.type ? selectedTypes.value.includes(entity.type) : false;
    });
  }

  // Filtrage par matériaux sélectionnés
  if (selectedMaterials.value.length > 0 || showUnspecifiedMaterials.value) {
    result = result.filter((entity: MaterialEntityWithMemos): boolean => {
      if (showUnspecifiedMaterials.value && !entity.material) return true;
      return entity.material ? selectedMaterials.value.includes(entity.material) : false;
    });
  }

  // Filtrage par recherche
  if (searchQuery.value) {
    result = result.filter((entity: MaterialEntityWithMemos): boolean => 
      searchInEntity(entity, searchQuery.value)
    );
  }

  // Tri
  result.sort((a: MaterialEntityWithMemos, b: MaterialEntityWithMemos): number => {
    let comparison: number = 0;
    
    switch (sortBy.value) {
      case 'appellation':
        comparison = (a.appellation || '').localeCompare(b.appellation || '');
        break;
      case 'type':
        comparison = (a.type || '').localeCompare(b.type || '');
        break;
      case 'material':
        comparison = (a.material || '').localeCompare(b.material || '');
        break;
      case 'date':
        comparison = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        break;
      case 'memos_count': {
        const aMemos = a.memos?.length || 0;
        const bMemos = b.memos?.length || 0;
        comparison = aMemos - bMemos;
        break;
      }
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });

  return result;
});

// Pagination avec types stricts
const totalPages: ComputedRef<number> = computed(() => 
  Math.ceil(filteredEntities.value.length / props.itemsPerPage)
);

const paginatedEntities: ComputedRef<MaterialEntityWithMemos[]> = computed(() => {
  const start: number = (currentPage.value - 1) * props.itemsPerPage;
  const end: number = start + props.itemsPerPage;
  return filteredEntities.value.slice(start, end);
});

// Watchers avec types stricts
watch([searchQuery, selectedTypes, selectedMaterials, sortBy], (): void => {
  currentPage.value = 1;
});

// Fermer les dropdowns en cliquant à l'extérieur
onMounted((): void => {
  const handleClickOutside = (event: Event): void => {
    const target = event.target as HTMLElement;
    if (!target.closest('.relative')) {
      showTypeDropdown.value = false;
      showMaterialDropdown.value = false;
    }
  };
  
  document.addEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.anamnesis-grid {
   display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.anamnesis-table {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  background: white;
}

.view-transition {
  transition: opacity 0.2s ease-in-out;
}

/* Styles pour la scrollbar des dropdowns */
.max-h-48 {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

.max-h-48::-webkit-scrollbar {
  width: 6px;
}

.max-h-48::-webkit-scrollbar-track {
  background: #f7fafc;
}

.max-h-48::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
}
</style> 